import { ButtonProps, useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BillGroupStatusEnum,
  IPaymentGateway,
  PaymentMethodShareRequest,
  PaymentMethodShareRequestSchema,
} from '@monetize/types/app';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { endOfDay } from 'date-fns/endOfDay';
import { formatISO } from 'date-fns/formatISO';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreatePaymentMethodShareLink } from '../../../api/accountsService';
import {
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MStack,
  MText,
} from '../../../components/Monetize';
import { CopyToClipboardButton } from '../../../components/Monetize/CopyToClipboardButton';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { BillGroupDropdown } from '../../../components/Monetize/Dropdowns/BillGroupDropdown';

interface PaymentMethodShareLinkModalProps {
  accountId: string;
  paymentGateway: IPaymentGateway;
  buttonProps?: ButtonProps;
}

export const PaymentMethodShareLinkModal = ({
  accountId,
  paymentGateway,
  buttonProps,
}: PaymentMethodShareLinkModalProps) => {
  const { maxExpiresAt, minExpiresAt } = useMemo(
    () => ({
      minExpiresAt: endOfDay(addDays(new Date(), 1)),
      maxExpiresAt: endOfDay(addMonths(new Date(), 2)),
    }),
    [],
  );

  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const {
    mutateAsync: createPaymentMethodShareLink,
    data: shareLinkData,
    isPending: isSaveLoading,
    reset: resetMutation,
  } = useCreatePaymentMethodShareLink({
    meta: { showErrorToast: true },
  });

  const {
    control,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<PaymentMethodShareRequest>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(PaymentMethodShareRequestSchema),
    defaultValues: {
      billGroupId: null,
      paymentGatewayId: paymentGateway.id,
      expiresAt: formatISO(addMonths(new Date(), 1), {
        representation: 'date',
      }),
    },
  });

  const onSubmit = async (payload: PaymentMethodShareRequest) => {
    try {
      payload.expiresAt = endOfDay(payload.expiresAt);
      await createPaymentMethodShareLink({
        accountId,
        payload,
      });
    } catch (ex) {
      // error handled in the mutation
    }
  };

  const handleClose = () => {
    resetMutation();
    onClose();
  };

  return (
    <>
      <MButton variant="secondary" onClick={onOpen} {...buttonProps}>
        Share External Link
      </MButton>
      {isOpen && (
        <MCenterModal
          isOpen={isOpen}
          onClose={handleClose}
          modalTitle={'Generate link to collect a new payment method'}
          size="sm"
          modalHeaderProps={{ display: 'flex', alignItems: 'center' }}
          closeOnEsc={false}
          closeOnOverlayClick={false}
          renderFooter={() => (
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
            >
              <MButton
                onClick={handleClose}
                variant="cancel"
                minW="auto"
                isDisabled={isSaveLoading}
              >
                Close
              </MButton>
              <MButton
                form="payment-method-share-link-form"
                variant="primary"
                isDisabled={!isValid || isSaveLoading || !!shareLinkData}
                isLoading={isSaveLoading}
                type="submit"
                minW="auto"
              >
                Generate Link
              </MButton>
            </MStack>
          )}
        >
          <form
            id="payment-method-share-link-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <MText mb={2}>
              Generate a link to share with your customer to allow them to add a
              new payment method to the Account.
            </MText>
            <MFormField
              label="Bill Group"
              tooltip="Optionally select which Bill Group you would like the payment method associated with."
              error={errors?.billGroupId}
            >
              <Controller
                name="billGroupId"
                control={control}
                render={({ field }) => (
                  <BillGroupDropdown
                    accountId={accountId}
                    statusFilter={[BillGroupStatusEnum.ACTIVE]}
                    field={field}
                    customSelectProps={{
                      clearable: true,
                      placeholder: 'Select a Bill Group',
                      isDisabled: isSaveLoading || !!shareLinkData,
                    }}
                  />
                )}
              />
            </MFormField>
            <MFormField
              mt={2}
              label="Expiration Date"
              isRequired
              tooltip="The link will auto-expire after a payment method is successfully added."
              error={errors?.expiresAt}
            >
              <Controller
                name="expiresAt"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    minDate={minExpiresAt}
                    maxDate={maxExpiresAt}
                    isDisabled={isSaveLoading || !!shareLinkData}
                    {...field}
                  />
                )}
              />
            </MFormField>
            <MFlex minH="32px" mt={6} mb={-2} justifyContent="end">
              {shareLinkData && (
                <CopyToClipboardButton
                  buttonProps={{ variant: 'primary' }}
                  value={shareLinkData.url}
                />
              )}
            </MFlex>
          </form>
        </MCenterModal>
      )}
    </>
  );
};
